/**
 * Authentication error handling utilities
 * Handles authentication-related errors and provides recovery mechanisms
 */

/**
 * Detects if an error is authentication-related
 * @param {any} error - The error to check
 * @returns {boolean} - True if it's an authentication error
 */
export const isAuthenticationError = (error) => {
    if (!error) return false;
    
    const errorMessage = error.message || String(error);
    
    return errorMessage.includes('Invalid authentication cookie') ||
           errorMessage.includes('generate_auth_cookie') ||
           errorMessage.includes('authentication') ||
           errorMessage.includes('cookie') ||
           errorMessage.includes('unauthorized') ||
           errorMessage.includes('forbidden') ||
           errorMessage.includes('token') ||
           errorMessage.includes('session');
};

/**
 * Clears all authentication data
 */
export const clearAuthenticationData = () => {
    try {
        if (typeof window === 'undefined') return;
        
        // Clear cookies
        const cookiesToClear = [
            'rbuser', 
            'current_resume', 
            'auth_token', 
            'session_id',
            'user_id',
            'login_hash',
            'remember_token'
        ];
        
        cookiesToClear.forEach(cookieName => {
            try {
                // Clear for current domain
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
                // Clear for parent domain
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.resume.org`;
                // Clear for www subdomain
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=www.resume.org`;
            } catch (e) {
                console.warn(`Could not clear cookie ${cookieName}:`, e);
            }
        });
        
        // Clear localStorage
        if (window.localStorage) {
            try {
                const authKeys = [
                    'user', 
                    'auth', 
                    'token', 
                    'session',
                    'resumes',
                    'offlineResumes',
                    'user_data',
                    'login_data'
                ];
                
                authKeys.forEach(key => {
                    if (localStorage.getItem(key)) {
                        localStorage.removeItem(key);
                    }
                });
            } catch (e) {
                console.warn('Could not clear localStorage auth data:', e);
            }
        }
        
        // Clear sessionStorage
        if (window.sessionStorage) {
            try {
                sessionStorage.clear();
            } catch (e) {
                console.warn('Could not clear sessionStorage:', e);
            }
        }
        
        console.log('Authentication data cleared successfully');
    } catch (error) {
        console.error('Error clearing authentication data:', error);
    }
};

/**
 * Redirects to login page
 * @param {string} redirectUrl - URL to redirect to after login
 */
export const redirectToLogin = (redirectUrl = null) => {
    try {
        if (typeof window === 'undefined') return;
        
        const currentUrl = window.location.href;
        const loginUrl = redirectUrl || '/login';
        
        // Add current URL as redirect parameter
        const separator = loginUrl.includes('?') ? '&' : '?';
        const finalUrl = `${loginUrl}${separator}redirect=${encodeURIComponent(currentUrl)}`;
        
        console.log('Redirecting to login:', finalUrl);
        window.location.href = finalUrl;
    } catch (error) {
        console.error('Error redirecting to login:', error);
        // Fallback redirect
        window.location.href = '/login';
    }
};

/**
 * Handles authentication errors with appropriate recovery actions
 * @param {Error} error - The authentication error
 * @param {Object} options - Options for handling the error
 */
export const handleAuthenticationError = (error, options = {}) => {
    try {
        const {
            clearData = true,
            redirect = true,
            showNotification = true,
            logToSentry = true
        } = options;
        
        console.warn('Authentication error detected:', error.message);
        
        // Clear authentication data if requested
        if (clearData) {
            clearAuthenticationData();
        }
        
        // Show user notification if requested
        if (showNotification && typeof window !== 'undefined') {
            // Try to show a user-friendly notification
            try {
                // Check if there's a notification system available
                if (window.showNotification) {
                    window.showNotification('Authentication error. Please log in again.', 'error');
                } else {
                    // Fallback to alert
                    alert('Authentication error. Please log in again.');
                }
            } catch (e) {
                console.warn('Could not show notification:', e);
            }
        }
        
        // Log to Sentry if requested
        if (logToSentry && window.Sentry) {
            window.Sentry.captureException(error, {
                tags: {
                    errorType: 'AUTHENTICATION_ERROR',
                    context: 'AUTH_ERROR_HANDLER',
                    action: 'AUTHENTICATION_FAILED'
                },
                extra: {
                    errorMessage: error.message,
                    userAgent: navigator.userAgent,
                    url: window.location.href
                }
            });
        }
        
        // Redirect to login if requested
        if (redirect) {
            redirectToLogin();
        }
    } catch (e) {
        console.error('Error handling authentication error:', e);
    }
};

/**
 * Wraps API calls with authentication error handling
 * @param {Function} apiCall - The API call function
 * @param {Object} options - Options for error handling
 * @returns {Function} - Wrapped API call function
 */
export const withAuthErrorHandling = (apiCall, options = {}) => {
    return async (...args) => {
        try {
            return await apiCall(...args);
        } catch (error) {
            if (isAuthenticationError(error)) {
                handleAuthenticationError(error, options);
                throw error; // Re-throw to let calling code handle it
            }
            throw error;
        }
    };
};

/**
 * Sets up global authentication error monitoring
 */
export const setupAuthErrorMonitoring = () => {
    // Monitor for authentication errors in fetch requests
    if (typeof window !== 'undefined') {
        const originalFetch = window.fetch;
        
        window.fetch = async (...args) => {
            try {
                const response = await originalFetch(...args);
                
                // Check for authentication errors in response
                if (response.status === 401 || response.status === 403) {
                    const responseText = await response.text();
                    if (isAuthenticationError({ message: responseText })) {
                        handleAuthenticationError(new Error(`HTTP ${response.status}: ${responseText}`));
                    }
                }
                
                return response;
            } catch (error) {
                if (isAuthenticationError(error)) {
                    handleAuthenticationError(error);
                }
                throw error;
            }
        };
        
        // Monitor for authentication errors in XMLHttpRequest
        const originalXHROpen = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function(method, url, ...args) {
            this.addEventListener('load', function() {
                if (this.status === 401 || this.status === 403) {
                    if (isAuthenticationError({ message: this.responseText })) {
                        handleAuthenticationError(new Error(`HTTP ${this.status}: ${this.responseText}`));
                    }
                }
            });
            
            return originalXHROpen.call(this, method, url, ...args);
        };
    }
};

/**
 * Creates a safe authentication wrapper for components
 * @param {React.Component} Component - The component to wrap
 * @returns {React.Component} - Wrapped component with auth error handling
 */
export const withAuthErrorBoundary = (Component) => {
    return class AuthErrorBoundary extends React.Component {
        constructor(props) {
            super(props);
            this.state = { hasError: false };
        }
        
        static getDerivedStateFromError(error) {
            if (isAuthenticationError(error)) {
                return { hasError: true };
            }
            return null;
        }
        
        componentDidCatch(error, errorInfo) {
            if (isAuthenticationError(error)) {
                handleAuthenticationError(error);
            }
        }
        
        render() {
            if (this.state.hasError) {
                return (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <h3>Authentication Error</h3>
                        <p>Please log in again to continue.</p>
                        <button onClick={() => window.location.href = '/login'}>
                            Go to Login
                        </button>
                    </div>
                );
            }
            
            return <Component {...this.props} />;
        }
    };
};

export default {
    isAuthenticationError,
    clearAuthenticationData,
    redirectToLogin,
    handleAuthenticationError,
    withAuthErrorHandling,
    setupAuthErrorMonitoring,
    withAuthErrorBoundary
};
